import {
  ArrowUp,
  Check,
  CircleStop,
  ExternalLink,
  Image as ImageIcon,
  History,
  Layers,
  Loader2,
  MessageSquarePlus,
  Paperclip,
  Pencil,
  Plug,
  RotateCcw,
  Sparkles,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../../lib/cn';
import { useIsWide } from '../../../lib/useMediaQuery';
import {
  Alert,
  Badge,
  Button,
  CheckboxControl,
  Drawer,
  DrawerContent,
  EmptyState,
  Hint,
  IconButton,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Pop,
  PopContent,
  PopTrigger,
  Segmented,
  StatusDot,
  notify,
} from '../../../ui';
import { useApiSources, useCreateDataSheetSource } from '../hooks';
import type { ScopeQuestion } from '../hooks/useChartSocket';
import { relativeTime } from '../lib/format';
import { parseWorkbookFile } from '../lib/sheetParse';
import { useAssistant } from './AssistantContext';

/* ============================================================================
   The assistant.

   This is an agent workflow, not a chat bubble: it plans, runs tools, writes
   charts onto the builder canvas and assembles whole dashboards. So the panel
   shows what it is doing (a progress trail, not one replaced line), what it
   produced (a link to the board or the chart), and gives the user the controls
   an agent needs — which agent to address, which board to edit, what to ground
   on, what design to match, and a way to stop.
   ========================================================================== */

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that image'));
    reader.readAsDataURL(file);
  });
}

/**
 * THE BUILD'S SCOPING ASK, ANSWERED BY CLICKING (AG-2329).
 *
 * `gap.questions` has always existed and has always been flat strings, so the
 * panel could only offer to LOAD one into the composer — the reader worked out
 * what was being asked and wrote the answer from scratch, which is the "doing by
 * hand what the machine already did" the block above it was written to stop.
 *
 * A question that arrives WITH its options is answered by picking. The options
 * are the agent's, derived from the fields it just read; nothing here knows what
 * a tab or a filter is called, and nothing here may.
 *
 * ⚠️ THE ANSWER GOES BACK AS PROSE, on purpose. The agent already reads an
 * instruction; composing the picks into one ("Tabs: Overview, Regional
 * performance. Filters: Region, Channel.") means no second parser, no new wire
 * contract for answers, and a reader who can edit the sentence before sending.
 */
export function ScopeAsk({ questions, connected, onCompose, onAnswer, onDecideForMe }: {
  questions: Array<string | ScopeQuestion>;
  connected: boolean;
  onCompose: (text: string) => void;
  onAnswer: (text: string) => void;
  onDecideForMe: () => void;
}) {
  const structured = questions.filter((q): q is ScopeQuestion => typeof q !== 'string');
  const plain = questions.filter((q): q is string => typeof q === 'string');
  const [picked, setPicked] = useState<Record<string, string[]>>({});

  const toggle = (q: ScopeQuestion, value: string) => {
    setPicked((prev) => {
      const had = prev[q.id] || [];
      if (q.kind === 'single') return { ...prev, [q.id]: had[0] === value ? [] : [value] };
      return {
        ...prev,
        [q.id]: had.includes(value) ? had.filter((v) => v !== value) : [...had, value],
      };
    });
  };

  const answer = structured
    .map((q) => {
      const chosen = (picked[q.id] || [])
        .map((v) => q.options?.find((o: { value: string }) => o.value === v)?.label || v);
      return chosen.length ? `${q.question} ${chosen.join(', ')}` : '';
    })
    .filter(Boolean)
    .join('\n');

  return (
    <div className="flex shrink-0 flex-col gap-2 border-t border-border-subtle px-3 py-2.5">
      {structured.map((q) => (
        <div key={q.id} className="flex flex-col gap-1">
          <p className="text-xs font-medium text-fg-secondary">{q.question}</p>
          {q.hint && <p className="text-[0.6875rem] text-fg-faint">{q.hint}</p>}
          <div className="flex flex-wrap gap-1">
            {(q.options || []).map((o: { value: string; label: string }) => {
              const on = (picked[q.id] || []).includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  aria-pressed={on}
                  disabled={!connected}
                  onClick={() => toggle(q, o.value)}
                  className={`rounded-full border px-2 py-1 text-[0.6875rem] disabled:opacity-50 ${
                    on
                      ? 'border-accent-border bg-accent-subtle text-fg'
                      : 'border-border bg-surface-subtle text-fg-secondary hover:border-accent-border'
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* A question with no options is still a question: it loads the composer,
          exactly as it did before this component existed. */}
      {plain.map((q) => (
        <button
          key={q}
          type="button"
          disabled={!connected}
          className="rounded-md border border-border bg-surface-subtle p-2 text-left hover:border-accent-border disabled:opacity-50"
          onClick={() => onCompose(`${q}\n\n`)}
        >
          <span className="block text-xs text-fg">{q}</span>
          <span className="mt-0.5 block text-[0.6875rem] text-fg-faint">Click to answer this one</span>
        </button>
      ))}

      {structured.length > 0 && (
        <div className="flex items-center justify-end gap-2 pt-0.5">
          {/* ⚠️ NEVER A DEAD END. A reader who does not want to scope the board
              says so and the build proceeds exactly as it did before any of this
              existed — which is also what an unattended caller gets. */}
          <button
            type="button"
            disabled={!connected}
            onClick={onDecideForMe}
            className="text-[0.6875rem] text-fg-faint hover:text-fg disabled:opacity-50"
          >
            Decide for me
          </button>
          <button
            type="button"
            disabled={!connected || !answer}
            onClick={() => onAnswer(answer)}
            className="rounded-md border border-accent-border bg-accent-subtle px-2 py-1 text-[0.6875rem] font-medium text-fg disabled:opacity-50"
          >
            Send answer
          </button>
        </div>
      )}
    </div>
  );
}


export function AssistantPanel() {
  const isWide = useIsWide();
  const { open, setOpen } = useAssistant();

  if (!open) return null;

  if (isWide) {
    return (
      <aside
        aria-label="Assistant"
        className="flex w-100 max-w-[92vw] shrink-0 flex-col border-l border-border bg-surface"
      >
        <AssistantBody onClose={() => setOpen(false)} />
      </aside>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent
        title="Assistant"
        width="26rem"
        className="[&>div:first-child]:hidden"
        bodyClassName="flex flex-col p-0"
      >
        <AssistantBody onClose={() => setOpen(false)} />
      </DrawerContent>
    </Drawer>
  );
}

function AssistantBody({ onClose }: { onClose: () => void }) {
  const {
    agent, target, setTarget, editBoard, setEditBoard, editingChartId, boundDashboard,
    threads, activeThreadId, newThread, openThread, deleteThread,
  } = useAssistant();
  const {
    connState,
    connected,
    connError,
    messages,
    progress,
    busy,
    suggestions,
    dashboard,
    gap,
    designReferences,
    send,
    sendDashboard,
    setDesignReference,
    stop,
    reconnect,
    clear,
  } = agent;

  const [draft, setDraft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const [refUrls, setRefUrls] = useState<string[]>([]);
  const [refImages, setRefImages] = useState<string[]>([]);
  const [refUrlDraft, setRefUrlDraft] = useState('');
  // A prompt composed before the socket opened. Never rendered — it exists only
  // to be sent once — so it is a ref, and flushing it costs no render.
  const pendingRef = useRef<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: sources } = useApiSources();
  const createSheet = useCreateDataSheetSource();

  // The board this turn edits: the one the agent just built, else the one on
  // screen. Without the second half, "Change with the assistant" on a board page
  // built a SECOND board instead of changing the one being looked at (AG-2039).
  const editTarget = dashboard?.id ? { id: dashboard.id, name: dashboard.name } : boundDashboard;
  const boardId = target === 'dashboard' && editBoard ? editTarget?.id : undefined;

  // Keep the transcript pinned to the newest turn.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, progress]);

  // A prompt composed before the socket was ready is sent as soon as it opens.
  useEffect(() => {
    const queued = pendingRef.current;
    if (connected && queued) {
      pendingRef.current = null;
      send(queued);
    }
  }, [connected, send]);

  // Push design references whenever the set changes, so the next build has them.
  const refSig = `${refUrls.join('|')}::${refImages.length}`;
  useEffect(() => {
    if (!connected) return;
    setDesignReference({ urls: refUrls, images: refImages });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refSig, connected]);

  const submit = () => {
    const text = draft.trim();
    if (!text || !connected || busy) return;
    if (target === 'dashboard') {
      sendDashboard(text, { dashboardId: boardId, sourceIds: sourceIds.length ? sourceIds : undefined });
    } else {
      send(text);
    }
    setDraft('');
  };

  const lastUserMessage = useMemo(
    () => [...messages].reverse().find((m) => m.role === 'user'),
    [messages],
  );

  /** Re-run the last question — the recovery path after a failed turn. */
  const retry = () => {
    if (!lastUserMessage || busy || !connected) return;
    if (target === 'dashboard') sendDashboard(lastUserMessage.text, { dashboardId: boardId });
    else send(lastUserMessage.text);
  };

  /** Put the last question back in the composer to reword it. */
  const editLast = () => {
    if (!lastUserMessage) return;
    setDraft(lastUserMessage.text);
    textareaRef.current?.focus();
  };

  // Same flow as "Add data source": parse the file, create a published Data
  // Studio table (so it is chartable), then hand the assistant its schema.
  const onFile = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const wb = await parseWorkbookFile(file);
      const sheet = wb.sheets[wb.sheetNames[0]];
      const name = file.name.replace(/\.[^.]+$/, '');
      const src = await createSheet.mutateAsync({
        tableName: name,
        fields: sheet.fields,
        rows: sheet.rows,
        publishedAsApi: true,
        apiMethod: 'GET',
      });
      notify.success(`Attached “${src.name}”`, {
        description: `${sheet.rows.length} rows · ${sheet.fields.length} columns — now a connected data source.`,
      });
      const cols = sheet.fields.map((f) => `${f.name} (${f.type})`).join(', ');
      const msg =
        `I attached "${src.name}" — ${sheet.rows.length} rows, ${sheet.fields.length} columns: ${cols}. ` +
        'Use it for this chart and suggest the best way to visualise it.';
      if (connected) send(msg);
      else pendingRef.current = msg;
    } catch (e) {
      notify.failure(e, 'Could not attach that file');
    } finally {
      setUploading(false);
    }
  };

  const onImage = async (file?: File | null) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      notify.error('That image is too large', { description: 'Reference images can be up to 4 MB.' });
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setRefImages((prev) => [...prev, dataUrl].slice(0, 4));
    } catch (e) {
      notify.failure(e, 'Could not read that image');
    }
  };

  const addRefUrl = () => {
    const url = refUrlDraft.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      notify.error('That does not look like a link', { description: 'Start with http:// or https://' });
      return;
    }
    setRefUrls((prev) => [...new Set([...prev, url])].slice(0, 4));
    setRefUrlDraft('');
  };

  const statusLine =
    connState === 'open'
      ? target === 'chart'
        ? 'Editing the chart on screen'
        : boardId
          ? `Editing “${dashboard?.name || 'this board'}”`
          : 'Building a new dashboard'
      : connState === 'connecting'
        ? 'Connecting…'
        : connState === 'error'
          ? 'Unavailable'
          : 'Disconnected';

  const referenceCount = refUrls.length + refImages.length;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* header */}
      <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-accent-subtle text-accent-text">
            <Sparkles className="size-4" />
          </span>
          <div className="min-w-0 leading-tight">
            <p className="text-[0.8125rem] font-semibold text-fg">Assistant</p>
            <p className="flex items-center gap-1.5 truncate text-xs text-fg-muted">
              <StatusDot
                tone={connState === 'open' ? 'success' : connState === 'error' ? 'danger' : 'neutral'}
                pulse={connState === 'connecting'}
              />
              {statusLine}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {/* A conversation used to end when you cleared it — the server still
              held it, but nothing here could name it, so it was gone (AG-2038).
              Starting a new one now leaves the old one in this list. */}
          {!editingChartId && (
            <>
              {messages.length > 0 && (
                <Hint content="New chat">
                  <IconButton label="New chat" size="sm" onClick={newThread}>
                    <MessageSquarePlus />
                  </IconButton>
                </Hint>
              )}
              <Menu>
                <MenuTrigger asChild>
                  <IconButton label="Chat history" size="sm" disabled={threads.length === 0}>
                    <History />
                  </IconButton>
                </MenuTrigger>
                <MenuContent align="end" className="max-w-[20rem]">
                  <MenuLabel>Your chats</MenuLabel>
                  {threads.map((t) => (
                    <MenuItem
                      key={t.id}
                      onSelect={() => openThread(t.id)}
                      className={t.id === activeThreadId ? 'font-medium' : undefined}
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{t.title || 'Untitled chat'}</span>
                        <span className="text-xs text-fg-muted">
                          {t.target === 'dashboard' ? 'Dashboard' : 'Chart'}
                          {t.updatedAt ? ` · ${relativeTime(new Date(t.updatedAt).toISOString())}` : ''}
                          {t.id === activeThreadId ? ' · open' : ''}
                        </span>
                      </span>
                    </MenuItem>
                  ))}
                  <MenuSeparator />
                  <MenuItem onSelect={() => deleteThread(activeThreadId ?? '')} disabled={!activeThreadId}>
                    Forget this chat
                  </MenuItem>
                </MenuContent>
              </Menu>
            </>
          )}
          {messages.length > 0 && editingChartId && (
            <Hint content="Clear this transcript">
              <IconButton label="Clear transcript" size="sm" onClick={clear}>
                <Trash2 />
              </IconButton>
            </Hint>
          )}
          <IconButton label="Close assistant" size="sm" onClick={onClose}>
            <X />
          </IconButton>
        </div>
      </div>

      {/* which agent, and what it should work on */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border-subtle px-3 py-2">
        <Segmented
          aria-label="What the assistant should work on"
          value={target}
          onValueChange={setTarget}
          size="sm"
          options={[
            { value: 'chart', label: 'One chart' },
            { value: 'dashboard', label: 'A dashboard' },
          ]}
        />

        {target === 'dashboard' && (
          <>
            {editTarget?.id && (
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-fg-secondary">
                <CheckboxControl
                  checked={editBoard}
                  onCheckedChange={(v) => setEditBoard(v === true)}
                  disabled={busy}
                  aria-label={`Keep editing ${editTarget.name || 'this board'}`}
                />
                Edit “{editTarget.name || 'this board'}”
              </label>
            )}

            <Pop>
              <PopTrigger asChild>
                <Button variant="ghost" size="sm" icon={<Plug />}>
                  {sourceIds.length ? `${sourceIds.length} source${sourceIds.length === 1 ? '' : 's'}` : 'Any source'}
                </Button>
              </PopTrigger>
              <PopContent className="w-72 p-0" align="start">
                <div className="border-b border-border-subtle px-3 py-2">
                  <p className="text-xs font-medium text-fg">Ground the build on</p>
                  <p className="mt-0.5 text-xs text-fg-muted">
                    Leave empty and the agent searches every connected source itself.
                  </p>
                </div>
                <div className="scroll-region max-h-56 p-1">
                  {(sources ?? []).map((s) => {
                    const on = sourceIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() =>
                          setSourceIds((prev) => (on ? prev.filter((x) => x !== s.id) : [...prev, s.id]))
                        }
                        className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs text-fg-secondary hover:bg-surface-hover"
                      >
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-xs border',
                            on ? 'border-accent bg-accent text-accent-fg' : 'border-border-strong',
                          )}
                        >
                          {on && <Check className="size-3" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{s.name}</span>
                      </button>
                    );
                  })}
                  {!sources?.length && (
                    <p className="px-2 py-4 text-center text-xs text-fg-muted">No connected sources yet.</p>
                  )}
                </div>
                {sourceIds.length > 0 && (
                  <div className="border-t border-border-subtle p-2">
                    <Button variant="ghost" size="sm" block onClick={() => setSourceIds([])}>
                      Clear selection
                    </Button>
                  </div>
                )}
              </PopContent>
            </Pop>

            <Pop>
              <PopTrigger asChild>
                <Button variant="ghost" size="sm" icon={<ImageIcon />}>
                  {referenceCount ? `${referenceCount} reference${referenceCount === 1 ? '' : 's'}` : 'Design'}
                </Button>
              </PopTrigger>
              <PopContent className="w-80" align="start">
                <p className="text-xs font-medium text-fg">Match a design</p>
                <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
                  Give the agent a page to look at or a screenshot to match. Websites are captured
                  server-side; images are sent as-is.
                </p>
                <div className="mt-3 flex gap-1.5">
                  <Input
                    size="sm"
                    value={refUrlDraft}
                    onChange={(e) => setRefUrlDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addRefUrl();
                      }
                    }}
                    placeholder="https://example.com/dashboard"
                    aria-label="Reference website"
                  />
                  <Button size="sm" variant="secondary" onClick={addRefUrl}>
                    Add
                  </Button>
                </div>
                {refUrls.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-1">
                    {refUrls.map((u) => (
                      <li key={u} className="flex items-center gap-1.5 text-xs text-fg-secondary">
                        <ExternalLink className="size-3 shrink-0 text-fg-faint" />
                        <span className="min-w-0 flex-1 truncate">{u}</span>
                        <IconButton
                          label={`Remove ${u}`}
                          size="sm"
                          onClick={() => setRefUrls((p) => p.filter((x) => x !== u))}
                        >
                          <X />
                        </IconButton>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 flex items-center gap-2">
                  <input
                    ref={imageRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="sr-only"
                    onChange={(e) => {
                      void onImage(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                  <Button size="sm" variant="secondary" icon={<ImageIcon />} onClick={() => imageRef.current?.click()}>
                    Add screenshot
                  </Button>
                  {refImages.length > 0 && (
                    <>
                      <span className="text-xs text-fg-muted">{refImages.length} attached</span>
                      <Button size="sm" variant="ghost" onClick={() => setRefImages([])}>
                        Clear
                      </Button>
                    </>
                  )}
                </div>
                {designReferences > 0 && (
                  <p className="mt-2 flex items-center gap-1 text-xs text-success">
                    <Check className="size-3" /> {designReferences} reference
                    {designReferences === 1 ? '' : 's'} loaded
                  </p>
                )}
              </PopContent>
            </Pop>
          </>
        )}
      </div>

      {connState === 'error' && connError && (
        <div className="shrink-0 px-3 pt-3">
          <Alert
            tone="warning"
            icon={TriangleAlert}
            title="The assistant is unavailable"
            actions={
              <Button size="sm" variant="secondary" icon={<RotateCcw />} onClick={reconnect}>
                Reconnect
              </Button>
            }
          >
            {connError}
          </Alert>
        </div>
      )}
      {connState === 'closed' && (
        <div className="shrink-0 px-3 pt-3">
          <Alert
            tone="neutral"
            title="Disconnected"
            actions={
              <Button size="sm" variant="secondary" icon={<RotateCcw />} onClick={reconnect}>
                Reconnect
              </Button>
            }
          >
            Your conversation is kept — reconnect to carry on.
          </Alert>
        </div>
      )}

      {/* transcript */}
      <div ref={scrollRef} className="scroll-region flex min-h-0 flex-1 flex-col gap-3 px-3 py-3">
        {messages.length === 0 && (
          <EmptyState
            icon={Sparkles}
            size="sm"
            title={target === 'chart' ? 'Describe the chart you want' : 'Describe the dashboard you want'}
            description={
              target === 'chart'
                ? 'Pick a data source in the builder (or attach a spreadsheet below), then ask in plain language — fields, chart type, colours, trend lines, filters. It renders on the canvas, where you can keep editing by hand.'
                : 'The agent finds the data, plans the tiles, binds every one to real fields and designs the layout. Say what decision the board is for and who reads it.'
            }
          />
        )}

        {messages.map((m) => (
          <div key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={cn(
                'max-w-[92%] whitespace-pre-wrap rounded-lg px-3 py-2 text-[0.8125rem] leading-relaxed',
                m.role === 'user'
                  ? 'bg-primary text-primary-fg'
                  : m.tone === 'error'
                    ? 'border border-danger-border bg-danger-bg text-danger'
                    : m.tone === 'stopped'
                      ? 'border border-border bg-surface-subtle text-fg-muted'
                      : 'bg-surface-hover text-fg',
              )}
            >
              {m.text}
              {m.streaming && (
                <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-fg-faint align-middle" />
              )}
            </div>
          </div>
        ))}

        {/* what it is doing right now — the tool trail, not one replaced line */}
        {progress.length > 0 && busy && (
          <ol className="flex flex-col gap-1 rounded-lg border border-border-subtle bg-surface-subtle p-2.5">
            {progress.map((step) => (
              <li key={step.id} className="flex items-center gap-2 text-xs">
                {step.done ? (
                  <Check className="size-3.5 shrink-0 text-success" />
                ) : (
                  <Loader2 className="size-3.5 shrink-0 animate-spin text-accent" />
                )}
                <span className={step.done ? 'text-fg-muted' : 'font-medium text-fg-secondary'}>
                  {step.label}
                </span>
              </li>
            ))}
          </ol>
        )}

        {/* what it produced */}
        {dashboard?.id && (
          <div className="rounded-lg border border-border bg-surface-subtle p-2.5">
            <p className="flex items-center gap-1.5 text-xs font-medium text-fg">
              <Layers className="size-3.5 text-fg-faint" />
              {dashboard.name || 'Dashboard'}
            </p>
            {!!dashboard.views?.length && (
              <p className="mt-1 flex flex-wrap gap-1">
                {dashboard.views.map((v) => (
                  <Badge key={v.id} tone="outline" size="sm">
                    {v.title}
                  </Badge>
                ))}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Button size="sm" variant="secondary" asChild>
                <Link to={`/dashboards/${dashboard.id}`}>Open board</Link>
              </Button>
              {dashboard.embedUrl && (
                <Button size="sm" variant="ghost" icon={<ExternalLink />} asChild>
                  <a href={dashboard.embedUrl} target="_blank" rel="noreferrer">
                    Public link
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* recovery for the last turn */}
        {!busy && lastUserMessage && messages.length > 1 && (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" icon={<RotateCcw />} onClick={retry} disabled={!connected}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" icon={<Pencil />} onClick={editLast}>
              Edit
            </Button>
          </div>
        )}
      </div>

      {/*
        A RECOVERY THAT RECOVERS.

        When grounding cannot choose, the agent already answers with the
        candidates it could not choose between and grounding's reason for each.
        The panel used to show only the paragraph, so the user's recovery was to
        type the name of a source they had just been shown — doing by hand the
        thing the machine had already done.

        `pick_source` has the answer in hand, so it gets buttons. `clarify` has
        the QUESTIONS in hand — `gap.questions` exists for exactly this — and used
        to render nothing at all, so the agent asked and the panel showed a
        paragraph. The reader then had to work out what was being asked and
        compose an answer from scratch, which is the same "doing by hand what the
        machine already did" this block was written to stop.

        A question is not an answer, so there is nothing to click THROUGH — but
        there is something to click INTO: each one loads the composer so the
        reader answers the question instead of restating it. `create_workflow`
        keeps its prose; it genuinely has nothing to offer.
      */}
      {gap && gap.recovery === 'pick_source' && gap.candidates.length > 0 && !busy && (
        <div className="flex shrink-0 flex-col gap-1.5 border-t border-border-subtle px-3 py-2.5">
          <p className="text-xs font-medium text-fg-secondary">Build it from which source?</p>
          {gap.candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={!connected}
              className="rounded-md border border-border bg-surface-subtle p-2 text-left hover:border-accent-border disabled:opacity-50"
              onClick={() => sendDashboard(gap.ask || 'Build it from this source', { sourceIds: [c.id] })}
            >
              <span className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-fg">{c.name}</span>
                {c.verdict && <Badge tone="outline" size="sm">{c.verdict}</Badge>}
              </span>
              {c.why && <span className="mt-0.5 block text-[0.6875rem] text-fg-faint">{c.why}</span>}
            </button>
          ))}
        </div>
      )}

      {gap && gap.questions.length > 0 && !busy && (
        <ScopeAsk
          questions={gap.questions}
          connected={connected}
          onCompose={(text) => {
            setDraft((d) => (d.trim() ? d : text));
            textareaRef.current?.focus();
          }}
          onAnswer={(text) => sendDashboard(text)}
          onDecideForMe={() => sendDashboard(gap.ask || 'Decide the scope yourself and build it')}
        />
      )}

      {/* suggestions */}
      {suggestions.length > 0 && !busy && (
        <div className="flex shrink-0 flex-wrap gap-1.5 border-t border-border-subtle px-3 py-2">
          {suggestions.map((s) => (
            <Button
              key={s.label}
              size="sm"
              variant="secondary"
              disabled={!connected}
              onClick={() => (target === 'dashboard' ? sendDashboard(s.prompt, { dashboardId: boardId }) : send(s.prompt))}
            >
              {s.label}
            </Button>
          ))}
        </div>
      )}

      {/* composer */}
      <div className="shrink-0 border-t border-border p-3">
        <div className="flex items-end gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.tsv,.xls,.xlsx,.ods,.json,application/json,text/csv"
            className="sr-only"
            onChange={(e) => {
              void onFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Hint content="Attach a spreadsheet as a data source">
            <IconButton
              label="Attach a spreadsheet"
              variant="secondary"
              size="lg"
              disabled={uploading || createSheet.isPending}
              onClick={() => fileRef.current?.click()}
            >
              {uploading || createSheet.isPending ? <Loader2 className="animate-spin" /> : <Paperclip />}
            </IconButton>
          </Hint>

          <label className="sr-only" htmlFor="assistant-composer">
            Message the assistant
          </label>
          <textarea
            id="assistant-composer"
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            rows={1}
            disabled={!connected}
            placeholder={
              !connected
                ? statusLine
                : target === 'chart'
                  ? 'Ask to build or change the chart…'
                  : boardId
                    ? 'Ask to change this dashboard…'
                    : 'Describe the dashboard you need…'
            }
            className={cn(
              'max-h-32 min-h-[2.625rem] flex-1 resize-none rounded-md border border-border bg-surface px-2.5 py-2 text-[0.8125rem]',
              'placeholder:text-fg-faint focus-visible:outline-2 focus-visible:outline-offset-[-1px] focus-visible:outline-focus',
              'disabled:cursor-not-allowed disabled:bg-surface-hover',
            )}
          />

          {busy ? (
            <Hint content="Stop this turn">
              <IconButton label="Stop" variant="secondary" size="lg" onClick={stop}>
                <CircleStop />
              </IconButton>
            </Hint>
          ) : (
            <IconButton
              label="Send"
              variant="primary"
              size="lg"
              disabled={!connected || !draft.trim()}
              onClick={submit}
            >
              <ArrowUp />
            </IconButton>
          )}
        </div>
        <p className="mt-1.5 text-[0.6875rem] text-fg-faint">
          Enter to send · Shift+Enter for a new line
        </p>
      </div>
    </div>
  );
}
